import { useCallback, useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router";
import { apiFetch } from "@/api";
import type { SiteInfo, SitesResponse } from "@/types";

// Per-surface remembered env (localStorage): Shipments and Orders each keep their
// own last-selected env; Home has no env. A legacy single key (from before this
// per-surface split) is read as a fallback so existing users keep their env once.
const LEGACY_STORAGE_KEY = "vlc.site";
type Surface = "shipments" | "orders";

function surfaceOf(pathname: string): Surface | null {
  if (pathname === "/shipments") return "shipments";
  if (pathname === "/orders") return "orders";
  return null;
}

function readLastEnv(surface: Surface | null): string | null {
  if (!surface) return null;
  try {
    return localStorage.getItem(`vlc.site.${surface}`) ?? localStorage.getItem(LEGACY_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeLastEnv(surface: Surface | null, id: string): void {
  if (!surface) return;
  try {
    localStorage.setItem(`vlc.site.${surface}`, id);
  } catch {
    /* best-effort */
  }
}

export interface UseSitesResult {
  status: "loading" | "no-access" | "ready" | "error";
  sites: SiteInfo[];
  selectedSite: string;
  selectedSiteInfo: SiteInfo | null;
  selectSite: (id: string) => void;
}

/**
 * Fetches the caller's accessible sites once, then resolves the active env for
 * the CURRENT surface — deliberately never auto-selecting a default:
 *   valid `?site=` (deep link) → this surface's remembered env → none.
 * A `none` result leaves the map behind <MapEnvOverlay>, prompting a pick. The
 * `?site=` param is only ever written on the data surfaces (never on `/`, so Home
 * stays clean), and selecting an env remembers it per-surface (localStorage), so
 * re-entering that surface restores it.
 */
export function useSites(): UseSitesResult {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sites, setSites] = useState<SiteInfo[]>([]);
  const [status, setStatus] = useState<"loading" | "no-access" | "ready" | "error">("loading");

  const surface = surfaceOf(location.pathname);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiFetch("/api/sites");
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const body = (await resp.json()) as SitesResponse;
        if (cancelled) return;
        if (!body.sites.length) {
          setStatus("no-access");
          return;
        }
        setSites(body.sites);
        setStatus("ready");
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const has = (id: string | null | undefined): id is string => !!id && sites.some((s) => s.project_id === id);

  const urlSite = searchParams.get("site");
  const remembered = readLastEnv(surface);
  // Resolution: valid URL param → this surface's remembered env → none.
  const selectedSite = has(urlSite) ? urlSite : has(remembered) ? remembered : "";
  const selectedSiteInfo = sites.find((s) => s.project_id === selectedSite) ?? null;

  // On a data surface with no valid `?site=`, adopt the remembered env and write
  // it into the URL so the address stays deep-linkable/shareable. Never runs on
  // Home (surface === null), so `/` keeps a clean URL.
  useEffect(() => {
    if (status !== "ready" || !surface || has(urlSite) || !has(remembered)) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("site", remembered);
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, surface, urlSite, sites]);

  const selectSite = useCallback(
    (id: string) => {
      writeLastEnv(surface, id);
      if (!surface) return; // Home: remember only (if ever wired), keep the URL clean
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("site", id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, surface],
  );

  return { status, sites, selectedSite, selectedSiteInfo, selectSite };
}
